import _taggedTemplateLiteral from "/app/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/taggedTemplateLiteral.js";

var _templateObject, _templateObject2, _templateObject3, _templateObject4;

import isPropValid from '@emotion/is-prop-valid';
import { Box, css, styled, Typography } from '@mui/material';
import { absurd, flow, pipe } from 'fp-ts/function';
import { Trans, useTranslation } from 'react-i18next';
import { Popover } from '../popover';
import { ProgressBar } from '../progress-bar';
import { AdvocacyProgress, ChallengeProgress, ChallengeTypeCompletionProgress, EventProgress, PointProgress } from './codecs';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";

var calculateOverlayHeight = function calculateOverlayHeight(_ref) {
  var badgeProgress = _ref.badgeProgress,
      size = _ref.size;
  return pipe(size === 'small' ? 5 : 8, function (badgeHeight) {
    return ChallengeProgress.is(badgeProgress) ? badgeHeight : (1 - badgeProgress.current / badgeProgress.totalRequired) * badgeHeight;
  });
};

var StyledImg = styled('img')(function (_ref2) {
  var size = _ref2.size,
      theme = _ref2.theme;
  return size === 'small' ? css(_templateObject || (_templateObject = _taggedTemplateLiteral(["\n        width: ", ";\n      "])), theme.spacing(5)) : size === 'large' ? css(_templateObject2 || (_templateObject2 = _taggedTemplateLiteral(["\n        width: ", ";\n      "])), theme.spacing(8)) : '';
});
var ProgressOverlay = styled('div', {
  shouldForwardProp: isPropValid
})(_templateObject3 || (_templateObject3 = _taggedTemplateLiteral(["\n  position: absolute;\n  top: 0;\n  left: 0;\n  background: white;\n  opacity: 0.7;\n  width: 100%;\n  height: ", ";\n"])), function (p) {
  return flow(calculateOverlayHeight, p.theme.spacing);
});

var BadgeImg = function BadgeImg(_ref3) {
  var _ref3$badgeProgressio = _ref3.badgeProgression,
      badge = _ref3$badgeProgressio.badge,
      badgeProgress = _ref3$badgeProgressio.badgeProgress,
      earnedDate = _ref3$badgeProgressio.earnedDate,
      _ref3$size = _ref3.size,
      size = _ref3$size === void 0 ? 'small' : _ref3$size;
  return /*#__PURE__*/_jsxs(Box, {
    sx: {
      position: 'relative'
    },
    "data-element": "Box",
    "data-component": "BadgeImg",
    "data-source-file": "index.tsx",
    children: [/*#__PURE__*/_jsx(StyledImg, {
      size: size,
      src: badge.imageUrl,
      alt: badge.name,
      "data-element": "StyledImg",
      "data-component": "BadgeImg",
      "data-source-file": "index.tsx"
    }), !earnedDate && badgeProgress && /*#__PURE__*/_jsx(ProgressOverlay, {
      badgeProgress: badgeProgress,
      size: size
    })]
  });
};

var BadgeProgressWrapper = styled('span')(_templateObject4 || (_templateObject4 = _taggedTemplateLiteral(["\n  font-weight: bold;\n"])));

var BadgeProgressDetails = function BadgeProgressDetails(_ref4) {
  var badgeProgress = _ref4.badgeProgress;

  var _useTranslation = useTranslation(),
      t = _useTranslation.t;

  if (EventProgress.is(badgeProgress) || ChallengeTypeCompletionProgress.is(badgeProgress)) {
    var _badgeProgress$challe;

    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(ProgressBar, {
        size: "medium",
        progress: badgeProgress.current / badgeProgress.totalRequired * 100
      }), /*#__PURE__*/_jsxs(Typography, {
        variant: "caption",
        children: [/*#__PURE__*/_jsx(Trans, {
          values: {
            progress: "".concat(badgeProgress.current, " / ").concat(badgeProgress.totalRequired),
            challengeType: EventProgress.is(badgeProgress) ? undefined : (_badgeProgress$challe = badgeProgress.challengeType) === null || _badgeProgress$challe === void 0 ? void 0 : _badgeProgress$challe.name
          },
          components: [/*#__PURE__*/_jsx(BadgeProgressWrapper, {})],
          shouldUnescape: true,
          children: EventProgress.is(badgeProgress) ? "<0>{{progress}}</0> completed" : "<0>{{progress}} {{challengeType}} Challenges</0> completed"
        }), ' - ', /*#__PURE__*/_jsx(Box, {
          component: "span",
          sx: {
            fontWeight: 'fontWeightBold'
          },
          children: t('{{remained}} more required', {
            remained: badgeProgress.totalRequired - badgeProgress.current
          })
        })]
      })]
    });
  }

  if (PointProgress.is(badgeProgress)) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(ProgressBar, {
        size: "medium",
        progress: badgeProgress.current / badgeProgress.totalRequired * 100
      }), /*#__PURE__*/_jsxs(Typography, {
        variant: "caption",
        children: [/*#__PURE__*/_jsx(Trans, {
          values: {
            progress: "".concat(badgeProgress.current, " / ").concat(badgeProgress.totalRequired)
          },
          components: [/*#__PURE__*/_jsx(BadgeProgressWrapper, {})],
          shouldUnescape: true,
          children: "<0>{{progress}} points</0> earned"
        }), ' - ', /*#__PURE__*/_jsx(Box, {
          component: "span",
          sx: {
            fontWeight: 'fontWeightBold'
          },
          children: t('{{remained}} more required', {
            remained: badgeProgress.totalRequired - badgeProgress.current
          })
        })]
      })]
    });
  }

  if (AdvocacyProgress.is(badgeProgress)) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(ProgressBar, {
        size: "medium",
        progress: badgeProgress.current / badgeProgress.totalRequired * 100
      }), /*#__PURE__*/_jsxs(Typography, {
        variant: "caption",
        children: [/*#__PURE__*/_jsx(Trans, {
          values: {
            progress: "".concat(badgeProgress.current, " / ").concat(badgeProgress.totalRequired)
          },
          components: [/*#__PURE__*/_jsx(BadgeProgressWrapper, {})],
          shouldUnescape: true,
          children: "<0>{{progress}} acts of advocacy</0> earned"
        }), ' - ', /*#__PURE__*/_jsx(Box, {
          component: "span",
          sx: {
            fontWeight: 'fontWeightBold'
          },
          children: t('{{remained}} more required', {
            remained: badgeProgress.totalRequired - badgeProgress.current
          })
        })]
      })]
    });
  }

  if (ChallengeProgress.is(badgeProgress)) {
    var _badgeProgress$challe2;

    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(ProgressBar, {
        size: "medium",
        progress: 0
      }), /*#__PURE__*/_jsx(Typography, {
        variant: "caption",
        children: /*#__PURE__*/_jsx(Trans, {
          values: {
            challenge: ((_badgeProgress$challe2 = badgeProgress.challenge) === null || _badgeProgress$challe2 === void 0 ? void 0 : _badgeProgress$challe2.headline) || t('challenge')
          },
          components: [/*#__PURE__*/_jsx(BadgeProgressWrapper, {})],
          shouldUnescape: true,
          children: "Complete <0>{{challenge}}</0>"
        })
      })]
    });
  }

  return absurd(badgeProgress);
};

var BadgePopoverContent = function BadgePopoverContent(_ref5) {
  var _ref5$badgeProgressio = _ref5.badgeProgression,
      badge = _ref5$badgeProgressio.badge,
      badgeProgress = _ref5$badgeProgressio.badgeProgress,
      earnedDate = _ref5$badgeProgressio.earnedDate,
      className = _ref5.className;
  return /*#__PURE__*/_jsxs(Box, {
    sx: {
      display: 'flex',
      flexDirection: 'column',
      maxWidth: 350
    },
    "data-element": "Box",
    "data-component": "BadgePopoverContent",
    "data-source-file": "index.tsx",
    children: [/*#__PURE__*/_jsxs(Box, {
      sx: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        mb: 1
      },
      "data-element": "Box",
      "data-component": "BadgePopoverContent",
      "data-source-file": "index.tsx",
      children: [/*#__PURE__*/_jsx(Box, {
        sx: {
          mr: 2
        },
        "data-element": "Box",
        "data-component": "BadgePopoverContent",
        "data-source-file": "index.tsx",
        children: /*#__PURE__*/_jsx(BadgeImg, {
          badgeProgression: {
            badge: badge,
            badgeProgress: null,
            earnedDate: earnedDate
          },
          size: "large",
          className: className,
          "data-element": "BadgeImg",
          "data-component": "BadgePopoverContent",
          "data-source-file": "index.tsx"
        })
      }), /*#__PURE__*/_jsx(Typography, {
        variant: "h2",
        "data-element": "Typography",
        "data-source-file": "index.tsx",
        children: badge.name
      })]
    }), /*#__PURE__*/_jsx(Typography, {
      variant: "body2",
      "data-element": "Typography",
      "data-source-file": "index.tsx",
      children: badge.description
    }), !earnedDate && badgeProgress && /*#__PURE__*/_jsx(Box, {
      sx: {
        mt: 1
      },
      children: /*#__PURE__*/_jsx(BadgeProgressDetails, {
        badgeProgress: badgeProgress
      })
    })]
  });
};

export var Badge = function Badge(_ref6) {
  var badgeProgression = _ref6.badgeProgression,
      size = _ref6.size,
      className = _ref6.className,
      _ref6$hasPopover = _ref6.hasPopover,
      hasPopover = _ref6$hasPopover === void 0 ? true : _ref6$hasPopover;
  return hasPopover ? /*#__PURE__*/_jsx(Popover, {
    sx: {
      maxWidth: {
        sm: '300px',
        md: 'unset'
      }
    },
    zIndex: function zIndex(t) {
      return t.zIndex.aboveModal;
    },
    keepMounted: false,
    content: function content() {
      return /*#__PURE__*/_jsx(BadgePopoverContent, {
        badgeProgression: badgeProgression
      });
    },
    children: /*#__PURE__*/_jsx(BadgeImg, {
      badgeProgression: badgeProgression,
      size: size,
      className: className
    })
  }) : /*#__PURE__*/_jsx(BadgeImg, {
    badgeProgression: badgeProgression,
    size: size,
    className: className
  });
};
export { BadgeProgress } from './codecs';